import { useCallback, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useIsMutating } from '@tanstack/react-query';
import { addMinutes } from 'date-fns';

import { Box, FlexBox } from '@sternum/styled-system';
import { addSnackbarAlert, Button, removeAllSnackbarAlerts } from '@sternum/ui';
import { Icon, Text } from '@sternum/web/components';
import { routes } from '@sternum/web/config/routes';
import { SpinnerIconGray } from '@sternum/web/icons';
import { namespaces } from '@sternum/web/services/i18n';
import { makeErrorMessage, TRPCReactQueryError } from '@sternum/web/services/trpc';
import { extractRateLimitFromError } from '@sternum/web/utils';

import { useMFATokenFromUrl, useSubmitMFA } from '../../hooks';
import { Countdown } from '../Countdown';
import { MFACodeInput } from '../MFACodeInput';
import { MFACodeTimer } from '../MFACodeTimer';

import { MFAFormValues } from './MFAForm.types';
import { ResendMFAButton, ResendMFAMutationKey } from './ResendMFAButton.component';

import { Container, FormBox } from './MFAForm.styles';

const defaultFormState: MFAFormValues = {
  code: '',
};

const getExpirationTime = (exp?: number) => {
  if (!exp) {
    return addMinutes(new Date(), 5);
  }

  if (Date.now() > exp) {
    return new Date();
  }

  return new Date(exp);
};

export interface MFAFormProps {
  /**
   * @default 6
   */
  codeLength?: number;
}

export function MFAForm({ codeLength = 6 }: MFAFormProps) {
  const { t } = useTranslation(namespaces.auth);
  const { token, email, exp } = useMFATokenFromUrl();

  const [expiresAt, setExpiresAt] = useState(getExpirationTime(exp));
  const [submitRemainingAttempts, setSubmitRemainingAttempts] = useState<number | null>(null);
  const isResendInProgress = !!useIsMutating({ mutationKey: [ResendMFAMutationKey] });
  const { mutateAsync: submitMFA } = useSubmitMFA();

  const [formDisabled, setFormDisabled] = useState(false);

  const { control, handleSubmit, formState, setError, clearErrors } = useForm<MFAFormValues>({
    defaultValues: defaultFormState,
    reValidateMode: 'onSubmit',
  });

  const handleFormSubmit: SubmitHandler<MFAFormValues> = async (values) => {
    try {
      await submitMFA({
        mfaToken: values.code,
        jwtToken: token,
      });
    } catch (err) {
      const error = err as TRPCReactQueryError;

      const { rateLimit, rateLimitRemaining, rateLimitReset } = extractRateLimitFromError(error);

      setSubmitRemainingAttempts(rateLimitRemaining);

      if (rateLimitRemaining > 0 && error?.data?.appCode !== 'auth.sso_permission_denied') {
        addSnackbarAlert({ variant: 'warning', message: t('mfaAttempts', { count: rateLimitRemaining }) });
      }

      if (rateLimitRemaining === 0) {
        removeAllSnackbarAlerts();
        addSnackbarAlert({
          variant: 'error',
          message: (
            <Countdown
              translationKey="mfaThrottled"
              initialValueInMs={rateLimitReset.getTime() - Date.now()}
              rateLimit={rateLimit}
            />
          ),
          displayTime: -1,
        });
      } else {
        const errorMessage = makeErrorMessage(error, t);

        setError('code', { message: errorMessage });
        addSnackbarAlert({ variant: 'error', message: errorMessage });
      }
    }
  };

  const handleTimeExpired = useCallback(() => {
    setFormDisabled(true);
  }, []);

  const handleResendSuccess = () => {
    setExpiresAt(getExpirationTime());
  };

  return (
    <Container>
      <Box mb={60}>
        <Button
          type="button"
          variant="text-secondary"
          noPadding
          as={Link}
          to={routes.auth.login.absolute}
          leftIcon={<Icon type="ChevronLeftIcon" size={12} />}
        >
          {t('backToLogIn')}
        </Button>
      </Box>

      <Text
        size={24}
        weight={600}
        color={{
          Dark: 'DarkerMiddle',
          Light: 'DarkerMiddle',
        }}
      >
        {t('mfaTitle')}
      </Text>

      <FormBox onSubmit={handleSubmit(handleFormSubmit)}>
        <Controller
          name="code"
          control={control}
          render={({ field }) => {
            const isCodeInputDisabled =
              isResendInProgress || formDisabled || formState.isSubmitting || submitRemainingAttempts === 0;

            return (
              <FlexBox gap={16} alignItems="center">
                <MFACodeInput
                  disabled={isCodeInputDisabled}
                  code={field.value}
                  codeLength={codeLength}
                  onChange={(value) => {
                    field.onChange({ target: { value } });

                    if (formState.errors.code) {
                      clearErrors('code');
                    }

                    if (value.length === codeLength) {
                      handleSubmit(handleFormSubmit)();
                    }
                  }}
                  hasError={!!formState.errors.code?.message}
                />

                {formState.isSubmitting && <SpinnerIconGray width={16} height={16} />}
              </FlexBox>
            );
          }}
        />

        <Box mt={16}>
          <Text size={12} lineHeight={18} color={{ Dark: 'GreyDarkest', Light: 'GreyDarkest' }}>
            <Trans t={t} i18nKey="mfaNotice" values={{ email }} components={{ strong: <strong />, br: <br /> }} />
          </Text>
        </Box>

        <Box mt={20}>
          <MFACodeTimer expiresAt={expiresAt} onExpired={handleTimeExpired} />
        </Box>

        <Box mt={24}>
          <ResendMFAButton disabled={formState.isSubmitting} onSuccess={handleResendSuccess} />
        </Box>
      </FormBox>
    </Container>
  );
}
